import { useMemo, useState } from "react";
import { useSearch } from "wouter";
import { isDraftMessage } from "@/lib/messages-tabs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Calendar, CheckCircle, Clock, HandCoins, Loader2, RefreshCw, Sparkles, Users, X, Zap } from "lucide-react";
import { PendingMessageRow } from "./_shared";
import { AUTO_DRAFT_SOURCE_VALUE, applyScheduleSourceFilter, deriveScheduleSources, isAutoDraftRow, ruleSourceValue } from "@/lib/schedule-source-filter";


// ─── Broadcast group derived from pending rows ────────────────────────────────
type BroadcastGroup = {
  broadcastId: string;
  sendAt: string | null;
  recipientCount: number;
  messagePreview: string;
  propertyName: string | null;
};

function deriveBroadcastGroups(rows: PendingMessageRow[]): BroadcastGroup[] {
  const map = new Map<string, BroadcastGroup>();
  for (const r of rows) {
    if (!r.isBroadcast || !r.broadcastId) continue;
    const existing = map.get(r.broadcastId);
    if (existing) {
      existing.recipientCount += 1;
    } else {
      map.set(r.broadcastId, {
        broadcastId: r.broadcastId,
        sendAt: r.sendAt ? String(r.sendAt) : null,
        recipientCount: 1,
        messagePreview: r.messageText ?? "",
        propertyName: r.propertyName ?? null,
      });
    }
  }
  // Sort by sendAt ascending
  return Array.from(map.values()).sort((a, b) => {
    const aT = a.sendAt ? new Date(a.sendAt).getTime() : 0;
    const bT = b.sendAt ? new Date(b.sendAt).getTime() : 0;
    return aT - bT;
  });
}


// ─── Scheduled Broadcasts section ────────────────────────────────────────────
function ScheduledBroadcastsSection({
  broadcasts,
  canManage,
  toast,
}: {
  broadcasts: BroadcastGroup[];
  canManage: boolean;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const queryClient = useQueryClient();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [reschedulingId, setReschedulingId] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleError, setRescheduleError] = useState("");

  const cancelBroadcastMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (broadcastId: string) =>
      apiRequest("POST", `/api/app/messages/broadcast/${broadcastId}/cancel`),
    onSuccess: () => {
      toast({ title: "Broadcast cancelled" });
      setConfirmingId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
    },
    onError: (err) => {
      setConfirmingId(null);
      toast({
        title: "Couldn't cancel broadcast",
        description: parseErrorMessage(err, "Couldn't cancel. Please try again."),
        variant: "destructive",
      });
    },
  });

  const rescheduleBroadcastMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (input: { broadcastId: string; sendAt: string }) =>
      apiRequest("PATCH", `/api/app/messages/broadcast/${input.broadcastId}`, { sendAt: input.sendAt }),
    onSuccess: () => {
      toast({ title: "Broadcast rescheduled" });
      setReschedulingId(null);
      setRescheduleDate("");
      setRescheduleTime("");
      setRescheduleError("");
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
    },
    onError: (err) => {
      toast({
        title: "Couldn't reschedule broadcast",
        description: parseErrorMessage(err, "Couldn't reschedule. Please try again."),
        variant: "destructive",
      });
    },
  });

  function openRescheduleBroadcast(b: BroadcastGroup) {
    setReschedulingId(b.broadcastId);
    setRescheduleDate(b.sendAt ? new Date(b.sendAt).toISOString().slice(0, 10) : "");
    setRescheduleTime(b.sendAt ? new Date(b.sendAt).toTimeString().slice(0, 5) : "");
    setRescheduleError("");
  }

  function handleRescheduleBroadcastConfirm() {
    if (!rescheduleDate || !reschedulingId) { setRescheduleError("Pick a date."); return; }
    const iso = `${rescheduleDate}T${rescheduleTime || "00:00"}`;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) { setRescheduleError("Invalid date/time."); return; }
    rescheduleBroadcastMutation.mutate({ broadcastId: reschedulingId, sendAt: dt.toISOString() });
  }

  if (broadcasts.length === 0) return null;

  return (
    <div className="mb-6" data-testid="section-scheduled-broadcasts">
      <div className="mb-2 flex items-center gap-2">
        <Users className="h-4 w-4 text-blue-500" />
        <h3 className="text-sm font-semibold text-gray-700">Scheduled Broadcasts</h3>
        <Badge variant="secondary" data-testid="badge-broadcast-count">{broadcasts.length}</Badge>
      </div>
      <div className="space-y-2">
        {broadcasts.map((b) => (
          <Card key={b.broadcastId} className="p-4" data-testid={`card-broadcast-${b.broadcastId}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <Calendar className="h-3.5 w-3.5" />
                  {b.sendAt ? new Date(b.sendAt).toLocaleString() : "(no send time)"}
                  <Badge variant="secondary">Scheduled</Badge>
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-blue-500" />
                    <span className="font-medium text-blue-700" data-testid={`text-broadcast-recipients-${b.broadcastId}`}>
                      {b.recipientCount} {b.recipientCount === 1 ? "recipient" : "recipients"}
                    </span>
                  </span>
                  {b.propertyName && (
                    <span className="text-gray-400">· {b.propertyName}</span>
                  )}
                </div>
                <div
                  className="line-clamp-2 whitespace-pre-wrap text-sm text-gray-900"
                  data-testid={`text-broadcast-preview-${b.broadcastId}`}
                >
                  {b.messagePreview}
                </div>
              </div>
              {canManage && (
                <div className="flex shrink-0 flex-col gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openRescheduleBroadcast(b)}
                    disabled={rescheduleBroadcastMutation.isPending || cancelBroadcastMutation.isPending}
                    data-testid={`button-reschedule-broadcast-${b.broadcastId}`}
                  >
                    <RefreshCw className="mr-1 h-3.5 w-3.5" /> Reschedule
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setConfirmingId(b.broadcastId)}
                    disabled={cancelBroadcastMutation.isPending || rescheduleBroadcastMutation.isPending}
                    data-testid={`button-cancel-broadcast-${b.broadcastId}`}
                  >
                    <X className="mr-1 h-3.5 w-3.5" /> Cancel
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Reschedule broadcast dialog */}
      <Dialog
        open={reschedulingId != null}
        onOpenChange={(v) => { if (!rescheduleBroadcastMutation.isPending) { if (!v) { setReschedulingId(null); setRescheduleError(""); } } }}
      >
        <DialogContent
          className="max-w-xs"
          data-testid="dialog-reschedule-broadcast"
          onInteractOutside={(e) => { if (rescheduleBroadcastMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (rescheduleBroadcastMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Reschedule broadcast</DialogTitle>
          </DialogHeader>
          {reschedulingId != null && (() => {
            const b = broadcasts.find((x) => x.broadcastId === reschedulingId);
            return b ? (
              <p className="text-sm text-gray-500">
                Updating send time for{" "}
                <strong>{b.recipientCount} {b.recipientCount === 1 ? "recipient" : "recipients"}</strong>.
              </p>
            ) : null;
          })()}
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-600">Date</Label>
              <Input
                type="date"
                value={rescheduleDate}
                onChange={(e) => { setRescheduleDate(e.target.value); setRescheduleError(""); }}
                className="mt-1"
                data-testid="input-reschedule-broadcast-date"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Time ({Intl.DateTimeFormat().resolvedOptions().timeZone})</Label>
              <Input
                type="time"
                value={rescheduleTime}
                onChange={(e) => { setRescheduleTime(e.target.value); setRescheduleError(""); }}
                className="mt-1"
                data-testid="input-reschedule-broadcast-time"
              />
            </div>
            {rescheduleError && <p className="text-xs text-red-500">{rescheduleError}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setReschedulingId(null); setRescheduleError(""); }}
              disabled={rescheduleBroadcastMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleRescheduleBroadcastConfirm}
              disabled={rescheduleBroadcastMutation.isPending}
              data-testid="button-reschedule-broadcast-confirm"
            >
              {rescheduleBroadcastMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm cancel dialog */}
      <Dialog
        open={confirmingId != null}
        onOpenChange={(v) => { if (!cancelBroadcastMutation.isPending) { if (!v) setConfirmingId(null); } }}
      >
        <DialogContent
          className="max-w-sm"
          data-testid="dialog-cancel-broadcast"
          onInteractOutside={(e) => { if (cancelBroadcastMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (cancelBroadcastMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Cancel scheduled broadcast?</DialogTitle>
          </DialogHeader>
          {confirmingId != null && (() => {
            const b = broadcasts.find((x) => x.broadcastId === confirmingId);
            return b ? (
              <p className="text-sm text-gray-600">
                This will cancel the broadcast scheduled for{" "}
                <strong>{b.sendAt ? new Date(b.sendAt).toLocaleString() : "unknown time"}</strong>{" "}
                to{" "}
                <strong>{b.recipientCount} {b.recipientCount === 1 ? "recipient" : "recipients"}</strong>.
                No messages will be sent.
              </p>
            ) : null;
          })()}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmingId(null)}
              disabled={cancelBroadcastMutation.isPending}
            >
              Keep it
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => { if (confirmingId) cancelBroadcastMutation.mutate(confirmingId); }}
              disabled={cancelBroadcastMutation.isPending}
              data-testid="button-confirm-cancel-broadcast"
            >
              {cancelBroadcastMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Cancel broadcast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Pending Tab (Awaiting + Scheduled) ────────────────────────────────────
export function PendingTab({
  filter,
  canManage,
  toast,
}: {
  filter: "awaiting" | "scheduled";
  canManage: boolean;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const queryClient = useQueryClient();
  const urlSearch = useSearch();
  const pendingQuery = useQuery<PendingMessageRow[]>({ queryKey: ["/api/app/messages/pending"] });
  // Reschedule picker state (keyed by row id so only one is open at a time)
  const [rescheduleId, setRescheduleId] = useState<number | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleError, setRescheduleError] = useState("");

  // Source filter: "all" | "manual" | "auto_draft" | "rule:<id>".
  // Seeded from the URL ?source= param so the disable-rule warning on the Rules
  // tab can deep-link straight to a given rule's scheduled messages (the rule
  // stays findable here even after it's been disabled). The seed is read once
  // on mount; the dropdown then drives local state.
  const [sourceFilter, setSourceFilter] = useState<string>(() => {
    if (filter !== "scheduled") return "all";
    const s = new URLSearchParams(urlSearch).get("source");
    return s && s.length > 0 ? s : "all";
  });

  const filtered = useMemo(() => {
    const rows = pendingQuery.data ?? [];
    if (filter === "awaiting") {
      // Awaiting: rule-originated rows that need host approval
      return rows.filter(isDraftMessage);
    }
    // Scheduled: auto-send rows with a future sendAt date
    const now = new Date();
    return rows.filter((r) => r.autoSend === true && r.sendAt != null && new Date(r.sendAt) > now);
  }, [pendingQuery.data, filter]);

  // Scheduled broadcasts — grouped by broadcastId, shown as a separate section
  // above individual scheduled messages in the "scheduled" view only.
  const scheduledBroadcasts = useMemo(
    () => (filter === "scheduled" ? deriveBroadcastGroups(filtered) : []),
    [filtered, filter],
  );

  // Individual scheduled rows: exclude rows that are part of a broadcast
  // (those are shown in the broadcasts section above).
  const individualFiltered = useMemo(
    () => (filter === "scheduled" ? filtered.filter((r) => !r.isBroadcast) : filtered),
    [filtered, filter],
  );

  // Distinct sources present in the individual rows (manual, auto-draft, each rule).
  const sources = useMemo(() => deriveScheduleSources(individualFiltered), [individualFiltered]);
  // Scheduled: always offered when there are rows. Awaiting: offered once
  // AI auto-drafts sit next to rule drafts, so hosts can split them apart.
  const showSourceFilter = individualFiltered.length > 0 &&
    (filter === "scheduled" || sources.autoDraftCount > 0);

  // Rows actually rendered after applying the source filter.
  const displayed = useMemo(
    () => applyScheduleSourceFilter(individualFiltered, sourceFilter),
    [individualFiltered, sourceFilter],
  );

  const cancelMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/messages/pending/${id}/cancel`),
    onSuccess: () => {
      toast({ title: "Cancelled" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
    },
    onError: (err) => toast({ title: "Couldn't cancel", description: parseErrorMessage(err, "Couldn't cancel. Please try again."), variant: "destructive" }),
  });

  const confirmMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/messages/pending/${id}/confirm`),
    onSuccess: () => {
      toast({ title: "Approved for send" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
    },
    onError: (err) => toast({ title: "Couldn't approve", description: parseErrorMessage(err, "Couldn't approve. Please try again."), variant: "destructive" }),
  });

  const rescheduleMutation = useMutation({
    mutationFn: async (input: { id: number; sendAt: string }) =>
      apiRequest("PATCH", `/api/app/messages/pending/${input.id}`, { sendAt: input.sendAt }),
    onSuccess: () => {
      toast({ title: "Rescheduled" });
      setRescheduleId(null);
      setRescheduleDate("");
      setRescheduleTime("");
      setRescheduleError("");
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
    },
    onError: (err) => toast({ title: "Couldn't reschedule", description: parseErrorMessage(err, "Couldn't reschedule. Please try again."), variant: "destructive" }),
  });

  function openReschedule(p: PendingMessageRow) {
    setRescheduleId(p.id);
    setRescheduleDate(p.sendAt ? new Date(p.sendAt).toISOString().slice(0, 10) : "");
    setRescheduleTime(p.sendAt ? new Date(p.sendAt).toTimeString().slice(0, 5) : "");
    setRescheduleError("");
  }

  function handleRescheduleConfirm() {
    if (!rescheduleDate || rescheduleId == null) { setRescheduleError("Pick a date."); return; }
    const iso = `${rescheduleDate}T${rescheduleTime || "00:00"}`;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) { setRescheduleError("Invalid date/time."); return; }
    rescheduleMutation.mutate({ id: rescheduleId, sendAt: dt.toISOString() });
  }

  const emptyMessage = filter === "awaiting"
    ? "No messages awaiting approval."
    : "No messages scheduled for auto-send.";

  const hasAnything = scheduledBroadcasts.length > 0 || displayed.length > 0;

  return (
    <div className="h-full overflow-auto p-6" data-testid={`tab-content-${filter}`}>
      {/* Scheduled Broadcasts section — only on the "scheduled" tab */}
      {filter === "scheduled" && !pendingQuery.isLoading && (
        <ScheduledBroadcastsSection
          broadcasts={scheduledBroadcasts}
          canManage={canManage}
          toast={toast}
        />
      )}

      {/* Individual scheduled / awaiting-approval messages */}
      {filter === "scheduled" && scheduledBroadcasts.length > 0 && individualFiltered.length > 0 && (
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-700">Individual Scheduled Messages</h3>
        </div>
      )}

      {showSourceFilter && (
        <div className="mb-4 flex items-center gap-2" data-testid="filter-schedule-source">
          <span className="text-xs font-medium text-gray-500">Source</span>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-8 w-[240px] text-xs" data-testid="select-schedule-source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="option-source-all">
                All sources ({sources.total})
              </SelectItem>
              {sources.manualCount > 0 && (
                <SelectItem value="manual" data-testid="option-source-manual">
                  Manual ({sources.manualCount})
                </SelectItem>
              )}
              {sources.autoDraftCount > 0 && (
                <SelectItem value={AUTO_DRAFT_SOURCE_VALUE} data-testid="option-source-auto-draft">
                  Auto-draft ({sources.autoDraftCount})
                </SelectItem>
              )}
              {sources.rules.map((r) => (
                <SelectItem
                  key={r.ruleId}
                  value={ruleSourceValue(r.ruleId)}
                  data-testid={`option-source-rule-${r.ruleId}`}
                >
                  {r.label} ({r.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {pendingQuery.isLoading ? (
        <div className="flex h-32 items-center justify-center text-gray-400">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !hasAnything ? (
        <Card className="p-8 text-center text-sm text-gray-400" data-testid="text-pending-empty">
          {individualFiltered.length > 0 && sourceFilter !== "all"
            ? "No messages from this source."
            : emptyMessage}
        </Card>
      ) : displayed.length === 0 && filter === "scheduled" && scheduledBroadcasts.length > 0 ? null : (
        <div className="space-y-2">
          {displayed.map((p) => (
            <Card key={p.id} className="p-4" data-testid={`card-pending-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <Calendar className="h-3.5 w-3.5" />
                    {p.sendAt ? new Date(p.sendAt).toLocaleString() : "(no send time)"}
                    {p.autoSend === false ? (
                      <Badge variant="outline">Awaiting</Badge>
                    ) : (
                      <Badge variant="secondary">Scheduled</Badge>
                    )}
                    {p.ruleName && (
                      <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-purple-500" />
                        <span className="font-medium text-purple-700">{p.ruleName}</span>
                      </span>
                    )}
                    {/* Task #4830 — collections notices have no messaging rule,
                        so without this they'd sit here with no origin shown. */}
                    {isAutoDraftRow(p) && (
                      <span className="flex items-center gap-1" data-testid={`pill-auto-draft-${p.id}`}>
                        <Sparkles className="h-3 w-3 text-violet-500" />
                        <span className="font-medium text-violet-700 dark:text-violet-300">Auto-draft</span>
                      </span>
                    )}
                    {p.automationSource === "collections" && (
                      <span className="flex items-center gap-1" data-testid={`pill-collections-${p.id}`}>
                        <HandCoins className="h-3 w-3 text-amber-600" />
                        <span className="font-medium text-amber-700">Collections Rule</span>
                      </span>
                    )}
                    <span>· {p.memberName ?? `Member #${p.memberId ?? "—"}`}</span>
                    {p.propertyName && <span className="text-gray-400">· {p.propertyName}</span>}
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-gray-900" data-testid={`text-pending-body-${p.id}`}>
                    {p.messageText}
                  </div>
                </div>
                {canManage && (
                  <div className="flex shrink-0 flex-col gap-2">
                    {p.autoSend === false && (
                      <Button
                        size="sm"
                        onClick={() => confirmMutation.mutate(p.id)}
                        disabled={confirmMutation.isPending}
                        data-testid={`button-confirm-${p.id}`}
                      >
                        <CheckCircle className="mr-1 h-3.5 w-3.5" /> Approve
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openReschedule(p)}
                      disabled={rescheduleMutation.isPending}
                      data-testid={`button-reschedule-${p.id}`}
                    >
                      <Clock className="mr-1 h-3.5 w-3.5" />
                      Reschedule
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => cancelMutation.mutate(p.id)}
                      disabled={cancelMutation.isPending}
                      data-testid={`button-cancel-${p.id}`}
                    >
                      <X className="mr-1 h-3.5 w-3.5" /> Cancel
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Reschedule date/time picker dialog */}
      <Dialog open={rescheduleId != null} onOpenChange={(v) => { if (!v) setRescheduleId(null); }}>
        <DialogContent className="max-w-xs" data-testid="dialog-reschedule">
          <DialogHeader>
            <DialogTitle>Reschedule message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-600">Date</Label>
              <Input
                type="date"
                value={rescheduleDate}
                onChange={(e) => { setRescheduleDate(e.target.value); setRescheduleError(""); }}
                className="mt-1"
                data-testid="input-reschedule-date"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Time ({Intl.DateTimeFormat().resolvedOptions().timeZone})</Label>
              <Input
                type="time"
                value={rescheduleTime}
                onChange={(e) => { setRescheduleTime(e.target.value); setRescheduleError(""); }}
                className="mt-1"
                data-testid="input-reschedule-time"
              />
            </div>
            {rescheduleError && <p className="text-xs text-red-500">{rescheduleError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRescheduleId(null)}>Cancel</Button>
            <Button size="sm" onClick={handleRescheduleConfirm} disabled={rescheduleMutation.isPending} data-testid="button-reschedule-confirm">
              {rescheduleMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
